import { useQuery } from "@tanstack/react-query";

import { ApiError, api } from "@/lib/api";
import type { Employee, Notification } from "@/lib/types";

/** Polls the unread notifications endpoint every 30s for the nav badge. */
export function useUnreadNotifications() {
  return useQuery({
    queryKey: ["notifications", "unread"],
    queryFn: async () => {
      const resp = await api.get<{ results: Notification[]; unread_count: number }>(
        "/notifications/inbox/?unread_only=true",
      );
      return resp.results;
    },
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });
}

// ---------------------------------------------------------------------------
// Leave balances
// ---------------------------------------------------------------------------

/** One row returned by `/leave/compute/balances/`. The wave-2 rewrite
 *  changed `leave_type` from the LeaveType UUID to its short `code`
 *  (e.g. "SL"); the human-readable name comes back as
 *  `leave_type_name`. All amount fields are decimal strings to keep
 *  the JSON precision identical to the DB column. */
export interface LeaveBalanceRow {
  leave_type: string;
  leave_type_name: string;
  total_allocated: string;
  leaves_taken: string;
  leaves_pending_approval: string;
  expired_leaves: string;
  /** Headline number — current ledger balance after taken + expired. */
  leave_balance: string;
  /** Balance MINUS pending applications — the more accurate "what's
   *  actually available to take" figure. */
  leave_balance_for_consumption: string;
  allocation_id: string | null;
}

export interface LeaveBalancesResponse {
  employee: string;
  as_of: string;
  balances: LeaveBalanceRow[];
}

/** Fetches every active leave-type balance for an employee in a single
 *  request — replaces the previous N-parallel-queries pattern from when
 *  the per-type `/leave/leave-ledger/balance/` endpoint existed.
 *
 *  Disabled until an employee id is present, so it doesn't fire while
 *  forms are still being populated. */
export function useEmployeeLeaveBalances(
  employee: string | undefined,
  asOf?: string,
) {
  return useQuery<LeaveBalancesResponse>({
    queryKey: ["leave-balances", employee, asOf ?? "today"],
    queryFn: () => {
      const params = new URLSearchParams({ employee: employee! });
      if (asOf) params.set("as_of", asOf);
      return api.get<LeaveBalancesResponse>(
        `/leave/compute/balances/?${params.toString()}`,
      );
    },
    enabled: Boolean(employee),
    staleTime: 10_000,
  });
}

// ---------------------------------------------------------------------------
// Self-service
// ---------------------------------------------------------------------------

/** Resolves the Employee row linked to the signed-in user.
 *
 * Returns ``data: null`` (not an error) when the user is staff/superuser
 * with no Employee record; UI code should treat that case as "no
 * employee context" rather than failing.
 */
export function useMyEmployee() {
  return useQuery<Employee | null>({
    queryKey: ["employees", "me"],
    queryFn: async () => {
      try {
        return await api.get<Employee>("/employees/employees/me/");
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          return null;
        }
        throw err;
      }
    },
    staleTime: 60_000,
  });
}
